/**
 * Role-Based Access Control (RBAC) Utilities
 * Defines permissions for each module and role
 */

// Finance Module Permissions
export const FINANCE_PERMISSIONS = {
  finance_director: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateInvoice: true,
    canApproveExpense: true,
    canManageVendors: true,
    canManageCustomers: true,
    canViewPayroll: true,
    canRunPayroll: true,
    canManageBankAccounts: true,
    canImportData: true,
    canExportData: true,
    canManageSettings: true,
    approvalLimit: Infinity,
    label: 'Finance Director'
  },
  finance_manager: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateInvoice: true,
    canApproveExpense: true,
    canManageVendors: true,
    canManageCustomers: true,
    canViewPayroll: true,
    canRunPayroll: false,
    canManageBankAccounts: true,
    canImportData: true,
    canExportData: true,
    canManageSettings: false,
    approvalLimit: 50000,
    label: 'Finance Manager'
  },
  accountant: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateInvoice: true,
    canApproveExpense: false,
    canManageVendors: true,
    canManageCustomers: true,
    canViewPayroll: false,
    canRunPayroll: false,
    canManageBankAccounts: false,
    canImportData: true,
    canExportData: true,
    canManageSettings: false,
    approvalLimit: 0,
    label: 'Accountant'
  },
  viewer: {
    canViewDashboard: true,
    canViewReports: true,
    canCreateInvoice: false,
    canApproveExpense: false,
    canManageVendors: false,
    canManageCustomers: false,
    canViewPayroll: false,
    canRunPayroll: false,
    canManageBankAccounts: false,
    canImportData: false,
    canExportData: true,
    canManageSettings: false,
    approvalLimit: 0,
    label: 'Viewer'
  }
};

// Docs Module Permissions
export const DOC_PERMISSIONS = {
  doc_admin: {
    canViewDashboard: true,
    canUploadDocuments: true,
    canDeleteDocuments: true,
    canManageAllDocs: true,
    canAnswerQueries: true,
    canManageUsers: true,
    canViewAuditLog: true,
    canBatchAnalyze: true,
    canConfigureSSO: true,
    canManageLicenses: true,
    canAccessAllDepartments: true,
    label: 'Doc Admin'
  },
  doc_manager: {
    canViewDashboard: true,
    canUploadDocuments: true,
    canDeleteDocuments: true,
    canManageAllDocs: true,
    canAnswerQueries: true,
    canManageUsers: false,
    canViewAuditLog: true,
    canBatchAnalyze: true,
    canConfigureSSO: false,
    canManageLicenses: false,
    canAccessAllDepartments: true,
    label: 'Doc Manager'
  },
  department_user: {
    canViewDashboard: true,
    canUploadDocuments: true,
    canDeleteDocuments: false,
    canManageAllDocs: false,
    canAnswerQueries: true,
    canManageUsers: false,
    canViewAuditLog: false,
    canBatchAnalyze: false,
    canConfigureSSO: false,
    canManageLicenses: false,
    canAccessAllDepartments: false,
    label: 'Department User'
  },
  chat_only: {
    canViewDashboard: true,
    canUploadDocuments: false,
    canDeleteDocuments: false,
    canManageAllDocs: false,
    canAnswerQueries: false,
    canManageUsers: false,
    canViewAuditLog: false,
    canBatchAnalyze: false,
    canConfigureSSO: false,
    canManageLicenses: false,
    canAccessAllDepartments: false,
    label: 'Chat Only'
  }
};

/**
 * Get user's finance permissions
 * @param {string} financeRole - User's finance role
 * @returns {object} Permission object
 */
export function getFinancePermissions(financeRole) {
  // Admin/super roles get full permissions
  if (['admin', 'super_admin', 'platform_admin', 'finance_admin'].includes(financeRole)) {
    return FINANCE_PERMISSIONS.finance_director;
  }
  return FINANCE_PERMISSIONS[financeRole] || FINANCE_PERMISSIONS.viewer;
}

/**
 * Get user's doc permissions
 * @param {string} docRole - User's doc role
 * @param {string} department - User's department (for department_user role)
 * @returns {object} Permission object with department info
 */
export function getDocPermissions(docRole, department = null) {
  // Admin/super roles get full permissions
  if (['admin', 'super_admin', 'platform_admin', 'doc_admin'].includes(docRole)) {
    return DOC_PERMISSIONS.doc_admin;
  }
  const permissions = DOC_PERMISSIONS[docRole] || DOC_PERMISSIONS.chat_only;
  return { ...permissions, department };
}

/**
 * Get current user from localStorage with their permissions
 * @returns {object} User object with permissions
 */
export function getCurrentUserWithPermissions() {
  try {
    const userStr = localStorage.getItem('userData');
    if (userStr) {
      const user = JSON.parse(userStr);
      return {
        ...user,
        financePermissions: getFinancePermissions(user.finance_role || user.role),
        docPermissions: getDocPermissions(user.doc_role || user.role, user.doc_department)
      };
    }
  } catch (e) {
    console.error('Error parsing user data:', e);
  }
  return null;
}

/**
 * Check if user has permission for a specific action
 * @param {string} module - 'finance' or 'docs'
 * @param {string} permission - Permission key to check
 * @returns {boolean}
 */
export function hasPermission(module, permission) {
  const user = getCurrentUserWithPermissions();
  if (!user) return false;
  
  if (module === 'finance') {
    return user.financePermissions?.[permission] === true;
  } else if (module === 'docs') {
    return user.docPermissions?.[permission] === true;
  }
  return false;
}

/**
 * Hook-friendly helper to get role badge
 * @param {string} role - Role value
 * @param {string} module - 'finance', 'docs', 'hr', 'sales'
 * @returns {object} { label, color }
 */
export function getRoleBadge(role, module) {
  const roleLabels = {
    finance: {
      finance_director: { label: 'Finance Director', color: 'bg-emerald-500' },
      finance_manager: { label: 'Finance Manager', color: 'bg-blue-500' },
      accountant: { label: 'Accountant', color: 'bg-purple-500' },
      viewer: { label: 'Viewer', color: 'bg-slate-500' }
    },
    docs: {
      doc_admin: { label: 'Doc Admin', color: 'bg-emerald-500' },
      doc_manager: { label: 'Doc Manager', color: 'bg-blue-500' },
      department_user: { label: 'Department User', color: 'bg-purple-500' },
      chat_only: { label: 'Chat Only', color: 'bg-slate-500' }
    }
  };
  
  return roleLabels[module]?.[role] || { label: role, color: 'bg-gray-500' };
}
